import React, { useState, useEffect, useRef } from "react";
import myAxios from "../AxiosUrl";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import uploadService from "../../services/uploadService";

// Custom Icons as SVG components
const UserIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" />
  </svg>
);

const PersonIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
);

const EmailIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.89 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
  </svg>
);

const LockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
  </svg>
);

const CameraIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 15.6c1.76 0 3.2-1.44 3.2-3.2s-1.44-3.2-3.2-3.2-3.2 1.44-3.2 3.2 1.44 3.2 3.2 3.2zm0-5.2c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm-8 .8H2.8c-.44 0-.8.36-.8.8v8.8c0 .44.36.8.8.8h18.4c.44 0 .8-.36.8-.8v-8.8c0-.44-.36-.8-.8-.8h-2.4l-1.83-2H7.83L6 11.2zm10-1.2l1.83 2h2.37v7.2H2.8V12h2.37L7 10h5z" />
  </svg>
);

const SaveIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z" />
  </svg>
);

const CancelIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z" />
  </svg>
);

// Avatar component
const Avatar = ({ user, size = 120, onImageUpload }) => {
  const [imageError, setImageError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const getInitials = (user) => {
    if (!user) return "A";
    if (user.firstName && user.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(
        0
      )}`.toUpperCase();
    } else if (user.firstName) {
      return user.firstName.charAt(0).toUpperCase();
    } else if (user.username) {
      return user.username.charAt(0).toUpperCase();
    } else if (user.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return "A";
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);

    try {
      // Step 1: Upload to CDN with custom filename
      const userId = user?.id || user?._id;
      const uploadResult = await uploadService.uploadWithStandardName(
        file,
        "profile",
        userId,
        (progress) => {
          console.log(`Upload progress: ${progress}%`);
        }
      );

      if (!uploadResult.success) {
        toast.error(uploadResult.error || "Failed to upload image");
        return;
      }

      // Step 2: Update profile with CDN URL
      const cdnUrl = uploadResult.url;
      console.log("CDN returned URL:", cdnUrl);
      console.log("Sending to API:", { profileImage: cdnUrl });

      const updateResponse = await myAxios.put("/profile", {
        profileImage: cdnUrl,
      });

      console.log("API response:", updateResponse.data);

      if (updateResponse.data.success || updateResponse.data.user) {
        toast.success("Profile image updated successfully!");
        // Update local user state with the CDN URL
        onImageUpload({
          profileImage: cdnUrl,
          profileImageUrl: cdnUrl,
        });
      } else {
        throw new Error(
          updateResponse.data.error || "Failed to update profile"
        );
      }
    } catch (error) {
      toast.error(error.message || "Failed to upload image");
      console.error("Image upload error:", error);
    } finally {
      setUploading(false);
    }
  };

  const avatarStyle = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: `${size * 0.4}px`,
    fontWeight: "600",
    color: "#ffffff",
    background: "linear-gradient(135deg, #667eea, #764ba2)",
    border: "4px solid rgba(255, 255, 255, 0.2)",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
    position: "relative",
    cursor: "pointer",
  };

  const overlayStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: "50%",
    background: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0,
    transition: "opacity 0.2s ease",
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: "600",
  };

  useEffect(() => {
    setImageError(false);
  }, [user?.profileImageUrl]);

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <div
        style={avatarStyle}
        onClick={() => fileInputRef.current?.click()}
        onMouseEnter={(e) => {
          const overlay = e.currentTarget.querySelector(".upload-overlay");
          if (overlay) overlay.style.opacity = "1";
        }}
        onMouseLeave={(e) => {
          const overlay = e.currentTarget.querySelector(".upload-overlay");
          if (overlay) overlay.style.opacity = "0";
        }}
      >
        {user?.profileImageUrl && !imageError ? (
          <img
            src={user.profileImageUrl}
            alt={user.email || "Profile"}
            style={{
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              objectFit: "cover",
            }}
            onError={() => setImageError(true)}
          />
        ) : (
          <div>{getInitials(user)}</div>
        )}

        <div className="upload-overlay" style={overlayStyle}>
          {uploading ? (
            <div
              style={{
                width: "20px",
                height: "20px",
                border: "2px solid #ffffff",
                borderTop: "2px solid transparent",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
          ) : (
            <div style={{ textAlign: "center" }}>
              <CameraIcon />
              <div style={{ fontSize: "12px", marginTop: "4px" }}>Change</div>
            </div>
          )}
        </div>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImageUpload}
        accept="image/*"
        style={{ display: "none" }}
        disabled={uploading}
      />
    </div>
  );
};

function EditProfile() {
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Personal Information Form State
  const [personalData, setPersonalData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    username: "",
    mobile: "",
  });
  const [personalErrors, setPersonalErrors] = useState({});

  // Password Form State
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordErrors, setPasswordErrors] = useState({});

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    fetchProfile();

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await myAxios.get("/profile");

      if (response.data.success) {
        const userData = response.data.data;
        setUser(userData);
        setPersonalData({
          firstName: userData.firstName || "",
          lastName: userData.lastName || "",
          email: userData.email || "",
          username: userData.username || "",
          mobile: userData.mobile || "",
        });
      }
    } catch (error) {
      toast.error("Failed to fetch profile data");
      console.error("Fetch profile error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePersonalInputChange = (e) => {
    const { name, value } = e.target;
    setPersonalData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (personalErrors[name]) {
      setPersonalErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (passwordErrors[name]) {
      setPasswordErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validatePersonalForm = () => {
    const newErrors = {};

    if (!personalData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!personalData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(personalData.email)) {
      newErrors.email = "Email is invalid";
    }

    setPersonalErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePasswordForm = () => {
    const newErrors = {};

    if (!passwordData.currentPassword) {
      newErrors.currentPassword = "Current password is required";
    }
    if (!passwordData.newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (passwordData.newPassword.length < 6) {
      newErrors.newPassword = "New password must be at least 6 characters";
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePersonalSubmit = async (e) => {
    e.preventDefault();

    if (!validatePersonalForm()) {
      return;
    }

    try {
      setLoading(true);
      const response = await myAxios.put("/profile", personalData);

      console.log("Personal update response:", response.data);

      if (response.data.success) {
        toast.success("Personal information updated successfully!");
        setUser(response.data.data);

        // Update personal data state with returned data
        setPersonalData({
          firstName: response.data.data.firstName || "",
          lastName: response.data.data.lastName || "",
          email: response.data.data.email || "",
          username: response.data.data.username || "",
          mobile: response.data.data.mobile || "",
        });
      } else {
        console.log("Response success is false or missing:", response.data);
        toast.error(
          "Update failed: " + (response.data.error || "Unknown error")
        );
      }
    } catch (error) {
      console.error("Personal update error:", error);
      const errorMessage =
        error.response?.data?.error || "Failed to update personal information";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!validatePasswordForm()) {
      return;
    }

    try {
      setPasswordLoading(true);
      const response = await myAxios.put("/profile", passwordData);

      console.log("Password update response:", response.data);

      if (response.data.success) {
        toast.success("Password changed successfully!");

        // Clear password form after successful update
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        setPasswordErrors({});
      } else {
        console.log("Response success is false or missing:", response.data);
        toast.error(
          "Password change failed: " + (response.data.error || "Unknown error")
        );
      }
    } catch (error) {
      console.error("Password update error:", error);
      const errorMessage =
        error.response?.data?.error || "Failed to change password";
      toast.error(errorMessage);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handlePersonalCancel = () => {
    if (user) {
      setPersonalData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        username: user.username || "",
        mobile: user.mobile || "",
      });
    }
    setPersonalErrors({});
    toast.info("Personal information changes cancelled");
  };

  const handlePasswordCancel = () => {
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setPasswordErrors({});
    toast.info("Password changes cancelled");
  };

  const handleImageUpload = (imageData) => {
    // Update user state with new image data
    setUser((prevUser) => ({
      ...prevUser,
      profileImage: imageData.profileImage,
      profileImageUrl: imageData.profileImageUrl,
    }));
  };

  if (loading && !user) {
    return (
      <div
        style={{
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          minHeight: "100vh",
          padding: "40px 20px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            borderRadius: "24px",
            padding: "80px",
            textAlign: "center",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div
            style={{
              width: "48px",
              height: "48px",
              border: "4px solid #e5e7eb",
              borderTop: "4px solid #667eea",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 24px",
            }}
          />
          <p
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#374151",
              margin: 0,
            }}
          >
            Loading profile...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        minHeight: "100vh",
        padding: isMobile ? "20px 10px" : "40px 20px",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{
          maxWidth: "900px",
          margin: "0 auto",
        }}
      >
        <div
          style={{
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            borderRadius: "24px",
            padding: isMobile ? "20px" : "32px",
            marginBottom: "32px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "24px",
              flexDirection: isMobile ? "column" : "row",
              textAlign: isMobile ? "center" : "left",
            }}
          >
            <div>
              <Avatar
                user={user}
                size={isMobile ? 100 : 120}
                onImageUpload={handleImageUpload}
              />
            </div>

            <div>
              <h1
                style={{
                  margin: 0,
                  fontSize: isMobile ? "24px" : "32px",
                  fontWeight: "800",
                  background: "linear-gradient(135deg, #667eea, #764ba2)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: "-0.02em",
                }}
              >
                Edit Profile
              </h1>
              <p
                style={{
                  margin: "4px 0 0 0",
                  color: "#6b7280",
                  fontSize: isMobile ? "14px" : "16px",
                  fontWeight: "500",
                }}
              >
                Update your account information
              </p>
            </div>
          </div>
        </div>

        {/* Personal Information Form */}
        <motion.form
          onSubmit={handlePersonalSubmit}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          style={{
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            borderRadius: "24px",
            padding: isMobile ? "24px" : "32px",
            marginBottom: "24px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "24px",
            }}
          >
            <PersonIcon />
            <h3
              style={{
                margin: 0,
                fontSize: "20px",
                fontWeight: "600",
                color: "#374151",
              }}
            >
              Personal Information
            </h3>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: "20px",
              marginBottom: "20px",
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                First Name *
              </label>
              <input
                type="text"
                name="firstName"
                value={personalData.firstName}
                onChange={handlePersonalInputChange}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: `2px solid ${
                    personalErrors.firstName ? "#ef4444" : "#e5e7eb"
                  }`,
                  borderRadius: "12px",
                  fontSize: "16px",
                  outline: "none",
                  transition: "border-color 0.2s ease",
                  boxSizing: "border-box",
                }}
                onFocus={(e) =>
                  !personalErrors.firstName &&
                  (e.target.style.borderColor = "#667eea")
                }
                onBlur={(e) =>
                  !personalErrors.firstName &&
                  (e.target.style.borderColor = "#e5e7eb")
                }
              />
              {personalErrors.firstName && (
                <p
                  style={{
                    color: "#ef4444",
                    fontSize: "12px",
                    margin: "4px 0 0 0",
                  }}
                >
                  {personalErrors.firstName}
                </p>
              )}
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Last Name
              </label>
              <input
                type="text"
                name="lastName"
                value={personalData.lastName}
                onChange={handlePersonalInputChange}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "2px solid #e5e7eb",
                  borderRadius: "12px",
                  fontSize: "16px",
                  outline: "none",
                  transition: "border-color 0.2s ease",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#667eea")}
                onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
              />
            </div>
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "600",
                color: "#374151",
              }}
            >
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={personalData.email}
              onChange={handlePersonalInputChange}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: `2px solid ${
                  personalErrors.email ? "#ef4444" : "#e5e7eb"
                }`,
                borderRadius: "12px",
                fontSize: "16px",
                outline: "none",
                transition: "border-color 0.2s ease",
                boxSizing: "border-box",
              }}
              onFocus={(e) =>
                !personalErrors.email &&
                (e.target.style.borderColor = "#667eea")
              }
              onBlur={(e) =>
                !personalErrors.email &&
                (e.target.style.borderColor = "#e5e7eb")
              }
            />
            {personalErrors.email && (
              <p
                style={{
                  color: "#ef4444",
                  fontSize: "12px",
                  margin: "4px 0 0 0",
                }}
              >
                {personalErrors.email}
              </p>
            )}
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "600",
                color: "#374151",
              }}
            >
              Username
            </label>
            <input
              type="text"
              name="username"
              value={personalData.username}
              onChange={handlePersonalInputChange}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "2px solid #e5e7eb",
                borderRadius: "12px",
                fontSize: "16px",
                outline: "none",
                transition: "border-color 0.2s ease",
                boxSizing: "border-box",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#667eea")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
            />
          </div>

          <div style={{ marginBottom: "24px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "600",
                color: "#374151",
              }}
            >
              Mobile
            </label>
            <input
              type="text"
              name="mobile"
              value={personalData.mobile}
              onChange={handlePersonalInputChange}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "2px solid #e5e7eb",
                borderRadius: "12px",
                fontSize: "16px",
                outline: "none",
                transition: "border-color 0.2s ease",
                boxSizing: "border-box",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#667eea")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
            />
          </div>

          <div
            style={{
              display: "flex",
              gap: "16px",
              justifyContent: "flex-end",
              flexDirection: isMobile ? "column-reverse" : "row",
            }}
          >
            <button
              type="button"
              onClick={handlePersonalCancel}
              style={{
                padding: "12px 24px",
                borderRadius: "12px",
                border: "2px solid #e5e7eb",
                background: "rgba(255, 255, 255, 0.9)",
                color: "#6b7280",
                fontSize: "16px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                flex: isMobile ? "1" : "auto",
              }}
            >
              <CancelIcon />
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: "12px 24px",
                borderRadius: "12px",
                border: "none",
                background: loading
                  ? "rgba(102, 126, 234, 0.7)"
                  : "linear-gradient(135deg, #667eea, #764ba2)",
                color: "#ffffff",
                fontSize: "16px",
                fontWeight: "600",
                cursor: loading ? "not-allowed" : "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                flex: isMobile ? "1" : "auto",
                boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
              }}
            >
              {loading ? (
                <div
                  style={{
                    width: "20px",
                    height: "20px",
                    border: "2px solid #ffffff",
                    borderTop: "2px solid transparent",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                />
              ) : (
                <SaveIcon />
              )}
              {loading ? "Saving..." : "Save Personal Info"}
            </button>
          </div>
        </motion.form>

        {/* Password Change Form */}
        <motion.form
          onSubmit={handlePasswordSubmit}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          style={{
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            borderRadius: "24px",
            padding: isMobile ? "24px" : "32px",
            marginBottom: "24px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "8px",
            }}
          >
            <LockIcon />
            <h3
              style={{
                margin: 0,
                fontSize: "20px",
                fontWeight: "600",
                color: "#374151",
              }}
            >
              Change Password
            </h3>
          </div>
          <p
            style={{
              margin: "0 0 24px 0",
              fontSize: "14px",
              color: "#6b7280",
            }}
          >
            Enter your current password and choose a new one
          </p>

          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "600",
                color: "#374151",
              }}
            >
              Current Password
            </label>
            <input
              type="password"
              name="currentPassword"
              value={passwordData.currentPassword}
              onChange={handlePasswordInputChange}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: `2px solid ${
                  passwordErrors.currentPassword ? "#ef4444" : "#e5e7eb"
                }`,
                borderRadius: "12px",
                fontSize: "16px",
                outline: "none",
                transition: "border-color 0.2s ease",
                boxSizing: "border-box",
              }}
              onFocus={(e) =>
                !passwordErrors.currentPassword &&
                (e.target.style.borderColor = "#667eea")
              }
              onBlur={(e) =>
                !passwordErrors.currentPassword &&
                (e.target.style.borderColor = "#e5e7eb")
              }
            />
            {passwordErrors.currentPassword && (
              <p
                style={{
                  color: "#ef4444",
                  fontSize: "12px",
                  margin: "4px 0 0 0",
                }}
              >
                {passwordErrors.currentPassword}
              </p>
            )}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: "20px",
              marginBottom: "24px",
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                New Password
              </label>
              <input
                type="password"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordInputChange}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: `2px solid ${
                    passwordErrors.newPassword ? "#ef4444" : "#e5e7eb"
                  }`,
                  borderRadius: "12px",
                  fontSize: "16px",
                  outline: "none",
                  transition: "border-color 0.2s ease",
                  boxSizing: "border-box",
                }}
                onFocus={(e) =>
                  !passwordErrors.newPassword &&
                  (e.target.style.borderColor = "#667eea")
                }
                onBlur={(e) =>
                  !passwordErrors.newPassword &&
                  (e.target.style.borderColor = "#e5e7eb")
                }
              />
              {passwordErrors.newPassword && (
                <p
                  style={{
                    color: "#ef4444",
                    fontSize: "12px",
                    margin: "4px 0 0 0",
                  }}
                >
                  {passwordErrors.newPassword}
                </p>
              )}
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Confirm New Password
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordInputChange}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: `2px solid ${
                    passwordErrors.confirmPassword ? "#ef4444" : "#e5e7eb"
                  }`,
                  borderRadius: "12px",
                  fontSize: "16px",
                  outline: "none",
                  transition: "border-color 0.2s ease",
                  boxSizing: "border-box",
                }}
                onFocus={(e) =>
                  !passwordErrors.confirmPassword &&
                  (e.target.style.borderColor = "#667eea")
                }
                onBlur={(e) =>
                  !passwordErrors.confirmPassword &&
                  (e.target.style.borderColor = "#e5e7eb")
                }
              />
              {passwordErrors.confirmPassword && (
                <p
                  style={{
                    color: "#ef4444",
                    fontSize: "12px",
                    margin: "4px 0 0 0",
                  }}
                >
                  {passwordErrors.confirmPassword}
                </p>
              )}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: "16px",
              justifyContent: "flex-end",
              flexDirection: isMobile ? "column-reverse" : "row",
            }}
          >
            <button
              type="button"
              onClick={handlePasswordCancel}
              style={{
                padding: "12px 24px",
                borderRadius: "12px",
                border: "2px solid #e5e7eb",
                background: "rgba(255, 255, 255, 0.9)",
                color: "#6b7280",
                fontSize: "16px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                flex: isMobile ? "1" : "auto",
              }}
            >
              <CancelIcon />
              Clear
            </button>
            <button
              type="submit"
              disabled={passwordLoading}
              style={{
                padding: "12px 24px",
                borderRadius: "12px",
                border: "none",
                background: passwordLoading
                  ? "rgba(220, 38, 38, 0.7)"
                  : "linear-gradient(135deg, #dc2626, #991b1b)",
                color: "#ffffff",
                fontSize: "16px",
                fontWeight: "600",
                cursor: passwordLoading ? "not-allowed" : "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                flex: isMobile ? "1" : "auto",
                boxShadow: "0 4px 12px rgba(220, 38, 38, 0.3)",
              }}
            >
              {passwordLoading ? (
                <div
                  style={{
                    width: "20px",
                    height: "20px",
                    border: "2px solid #ffffff",
                    borderTop: "2px solid transparent",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                />
              ) : (
                <LockIcon />
              )}
              {passwordLoading ? "Changing..." : "Change Password"}
            </button>
          </div>
        </motion.form>
      </motion.div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}

export default EditProfile;
